import React, { memo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import { useOnScreen } from 'site-modules/shared/hooks/use-on-screen';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { InventoryEntities } from 'client/data/models/inventory';
import { VdpModule } from 'site-modules/shared/components/inventory/vdp-module/vdp-module';
import { isNew } from 'site-modules/shared/utils/inventory-utils/is-new';
import { WarrantyDetailsNew } from 'site-modules/shared/components/inventory/warranty-module/warranty-details-new/warranty-details-new';
import { WarrantyDetailsUsed } from 'site-modules/shared/components/inventory/warranty-module/warranty-details-used/warranty-details-used';
import { DATA_PATH } from 'client/engagement-handlers/inventory-engagement-handler/constants';

export const WarrantyModule = memo(({ vinData, className = '', ...rest }) => {
  const [animateWarrantyPeriodProgress, setAnimateWarrantyPeriodProgress] = useToggle(false, { limit: 1 });

  const warrantyModulRef = useRef();
  const [isModuleVisible] = useOnScreen(warrantyModulRef, { threshold: 1.0 });
  const vinType = get(vinData, DATA_PATH.INVENTORY_TYPE, '');

  useEffect(() => {
    if (isModuleVisible) {
      setAnimateWarrantyPeriodProgress(true);
    }
  }, [isModuleVisible, setAnimateWarrantyPeriodProgress]);

  return (
    <VdpModule
      sectionTitle="Included Manufacturer Warranties"
      subCopy={
        <span className="text-cool-gray-40">
          Most warranties expire when the vehicle exceeds the coverage for <strong>years or miles</strong>, whichever
          comes first.
        </span>
      }
      className={classnames('bg-cool-gray-90', className)}
    >
      <div data-tracking-parent="edm-entry-warranty" ref={warrantyModulRef}>
        {isNew(vinType) ? (
          <WarrantyDetailsNew {...rest} />
        ) : (
          <WarrantyDetailsUsed
            vinData={vinData}
            {...rest}
            animateWarrantyPeriodProgress={animateWarrantyPeriodProgress}
          />
        )}
      </div>
    </VdpModule>
  );
});

WarrantyModule.propTypes = {
  warranty: InventoryEntities.Warranty.isRequired,
  vinData: InventoryEntities.InventoryVin.isRequired,
  className: PropTypes.string,
};

WarrantyModule.displayName = 'WarrantyModule';
